import { useState } from "react";
import Swal from 'sweetalert2';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Fade from '@mui/material/Fade';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import CustomIconButton from "../custom_components/CustomIconButton";

const STATUSES = [
    {
        value: "Arrived",
        label: "Mark as Arrived",
        icon: <CheckCircleOutlineIcon fontSize="small" />,
        color: "#2e7d32",
        destructive: false,
    },
    {
        value: "Shipped",
        label: "Mark as Shipped",
        icon: <LocalShippingOutlinedIcon fontSize="small" />,
        color: "#1565c0",
        destructive: false,
    },
    {
        value: "Forfeited",
        label: "Mark as Forfeited",
        icon: <BlockOutlinedIcon fontSize="small" />,
        color: "#c62828",
        destructive: true,
    },
    {
        value: "Disposed",
        label: "Mark as Disposed",
        icon: <DeleteOutlineIcon fontSize="small" />,
        color: "#e65100", 
        destructive: true,
    },
];

export default function MenuOrderStatus({ orderInfo, onUpdate }) {

    const apiUrl = process.env.REACT_APP_API_URL;

    const [anchorEl, setAnchorEl] = useState(null);

    const open = Boolean(anchorEl);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const [isLoading, setIsLoading] = useState(false);

    // SET ORDER STATUS
    const handleSetOrderStatus = async (valStat) => {
        setIsLoading(true);
        handleClose();

        try {
            const response = await fetch(`${apiUrl}manage_arrived/update_order_status.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    customerID: orderInfo.CustomerID,
                    orderID: orderInfo.OrderID,
                    valueStatus: valStat
                })
            });

            const result = await response.json();

            if (result.status === 'success') {

                setIsLoading(false);

                onUpdate({
                    customerID: orderInfo.CustomerID,
                    orderID: orderInfo.OrderID,
                    status: valStat,
                });

            } else if (result.status === 'error') {
                Swal.fire({ title: 'Server Error!', text: result.message, icon: 'error' });
            }
        } catch (error) {
            Swal.fire({ title: 'Server Error!', text: error, icon: 'error' });
            console.error('Error getting orders:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSelect = async (status) => {

        handleClose();

        if (status.destructive) {

            const result = await Swal.fire({
                title: `Mark as ${status.value.toLowerCase()}?`,
                html: `
                This action cannot be undone.<br/><br/>
                The order will be marked as
                <strong>${status.value.toLowerCase()}</strong>.
            `,
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Confirm",
                cancelButtonText: "Cancel",
                reverseButtons: true,
                confirmButtonColor: "#DC2626",
            });

            if (result.isConfirmed) {
                handleSetOrderStatus(status.value);
            }

            return;
        }

        handleSetOrderStatus(status.value);
    };

    const firstDestructiveIndex =
        STATUSES.findIndex((s) => s.destructive);

    return (
        <>
            <CustomIconButton
                type="info"
                loading={isLoading}
                icon={<MoreVertIcon fontSize="small" />}
                onClick={handleClick}
            />

            <Menu
                aria-hidden="false"
                id="order-status-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slots={{ transition: Fade }}
                slotProps={{
                    list: {
                        "aria-labelledby": "order-menu-button",
                    },
                }}
                transformOrigin={{
                    horizontal: "right",
                    vertical: "top",
                }}
                anchorOrigin={{
                    horizontal: "right",
                    vertical: "bottom",
                }}
            >

                {STATUSES.map((status, index) => [
                    index === firstDestructiveIndex && (
                        <Divider
                            key={`divider-${status.value}`}
                            sx={{ my: 0.5 }}
                        />
                    ),

                    orderInfo.OrderStat !== status.value && (
                        <MenuItem
                            key={status.value}
                            onClick={() => handleSelect(status)}
                            sx={{
                                color: status.destructive
                                    ? status.color
                                    : "text.primary",

                                "&:hover": {
                                    backgroundColor: status.destructive
                                        ? `${status.color}12`
                                        : undefined,
                                },
                            }}
                        >
                            <ListItemIcon sx={{ color: status.color }}>
                                {status.icon}
                            </ListItemIcon>

                            <ListItemText>
                                {status.label}
                            </ListItemText>
                        </MenuItem>
                    ),
                ])}

            </Menu>
        </>
    );
}